import React, { useState, useEffect, useRef } from "react";
import { FaArrowRight, FaCopy, FaCheck, FaArrowLeft, FaArrowRight as FaNext } from "react-icons/fa";
import "./JavaScript.css";

function JavaScript() {
  const text = "Welcome back Nodejs Dev";
  const [displayedText, setDisplayedText] = useState("");
  const [charIndex, setCharIndex] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [copied, setCopied] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (charIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(text.substring(0, charIndex + 1));
        setCharIndex(charIndex + 1);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [charIndex, text]);

  const services = [
    {
      id: 1,
      title: "M-Pesa Integration",
      content: `
  ### PAYBILL/TILL APPLICATION PROCESS
  
  Before we go through the application process, let's answer the question: **Which one should you choose? Paybill or Till?**
  
  A short answer: **It depends.**
  
  If you intend to integrate online M-Pesa payments into your system, the application requirements for Paybill and Till (BuyGoods) are almost similar. The choice between Paybill and Till depends on how you intend to use it.
  
  - If your website requires only online payments (e.g., via STK Push), you can go for a Till.
  - If you need to track offline payments at any point, a Paybill is the ideal choice because it provides an account number option that acts as a unique identifier for your transactions.
  
  **Note:** Individual Till and Bank Paybills cannot be used for M-Pesa integrations. To integrate M-Pesa online payments with a Bank Paybill or Personal Till, you can use a service like Lipia Online.
  
  Once you're done, visit the Daraja M-Pesa Portal, create an App, and you'll receive the **Consumer Key**, **Consumer Secret**, and **PassKey**. For live apps, the **PassKey** will be sent via email immediately after going live.
  
  Now, let's dive into the actual Node.js code.
  
  ---
  
  ### GET ACCESS TOKEN
  
  To interact with the M-Pesa API, you first need to generate an access token. Here's how you can do it in Node.js:
  
  \`\`\`javascript
  const axios = require('axios');
  const { Buffer } = require('buffer');
  
  async function getAccessToken() {
    const consumerKey = 'yourConsumerKey';
    const consumerSecret = 'yourConsumerSecret';
  
    // Choose one depending on your development environment
    const url = 'https://sandbox.safaricom.co.ke/oauth/v1/generate?grant_type=client_credentials'; // Sandbox
    // const url = 'https://api.safaricom.co.ke/oauth/v1/generate?grant_type=client_credentials'; // Live
  
    const credentials = Buffer.from(\`\${consumerKey}:\${consumerSecret}\`).toString('base64');
  
    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: \`Basic \${credentials}\`,
        },
      });
  
      if (response.data.access_token) {
        return response.data.access_token;
      } else {
        throw new Error('Failed to get access token.');
      }
    } catch (error) {
      throw new Error(\`Failed to get access token: \${error.message}\`);
    }
  }
  \`\`\`
  
  ---
  
  ### SEND STK PUSH
  
  Once you have the access token, you can initiate an STK Push to request payment from a customer. Here's how you can do it in Node.js:
  
  \`\`\`javascript
  async function sendStkPush() {
    const token = await getAccessToken();
    const timestamp = new Date().toISOString().replace(/[^0-9]/g, '').slice(0, -3);
  
    const shortCode = 'YOUR_PAYBILL'; // Sandbox: 174379
    const passkey = 'YOUR_PASSKEY';
    const stkPassword = Buffer.from(\`\${shortCode}\${passkey}\${timestamp}\`).toString('base64');
  
    const url = 'https://sandbox.safaricom.co.ke/mpesa/stkpush/v1/processrequest'; // Sandbox
    // const url = 'https://api.safaricom.co.ke/mpesa/stkpush/v1/processrequest'; // Live
  
    const requestBody = {
      BusinessShortCode: shortCode,
      Password: stkPassword,
      Timestamp: timestamp,
      TransactionType: 'CustomerPayBillOnline', // For Till: 'CustomerBuyGoodsOnline'
      Amount: '10',
      PartyA: '254708374149',
      PartyB: shortCode,
      PhoneNumber: '254708374149',
      CallBackURL: 'https://yourwebsite.co.ke/callbackurl',
      AccountReference: 'account',
      TransactionDesc: 'test',
    };
  
    try {
      const response = await axios.post(url, requestBody, {
        headers: {
          Authorization: \`Bearer \${token}\`,
          'Content-Type': 'application/json',
        },
      });
  
      console.log(response.data);
      return response.data;
    } catch (error) {
      console.error(\`Error: \${error.message}\`);
    }
  }
  \`\`\`
  
  ---
  
  ### HANDLING CALLBACK DATA
  
  After sending the STK Push, you need to handle the callback data from M-Pesa. Here's how you can handle it in Node.js:
  
  \`\`\`javascript
  const fs = require('fs');
  const path = require('path');
  
  function handleCallbackData(requestBody) {
    const callbackData = requestBody.Body.stkCallback;
  
    // Save the callback data to a file
    const filename = \`callback_data_\${Date.now()}.json\`;
    const filePath = path.join(__dirname, 'callbackData', filename);
  
    fs.writeFileSync(filePath, JSON.stringify(callbackData, null, 2));
  
    // Validate the callback data
    if (callbackData.ResultCode !== 0) {
      console.error(\`Transaction failed: \${callbackData.ResultDesc}\`);
      return { ResultCode: callbackData.ResultCode, ResultDesc: callbackData.ResultDesc };
    }
  
    // Extract relevant data
    const metadata = callbackData.CallbackMetadata.Item;
    const amount = metadata.find((item) => item.Name === 'Amount').Value;
    const phoneNumber = metadata.find((item) => item.Name === 'PhoneNumber').Value;
    const mpesaReceiptNumber = metadata.find((item) => item.Name === 'MpesaReceiptNumber').Value;
  
    // Save the data to a database or perform other actions
    console.log(\`Payment received: \${amount} from \${phoneNumber}, Receipt: \${mpesaReceiptNumber}\`);
  
    return { ResultCode: 0, ResultDesc: 'Success' };
  }
  \`\`\`
  
  ---
  
  ### HANDLING OFFLINE PAYBILL/TILL PAYMENTS
  
  To handle offline payments, you need to register URLs for validation and confirmation. Here's how you can do it in Node.js:
  
  \`\`\`javascript
  async function registerURLs() {
    const token = await getAccessToken();
    const url = 'https://sandbox.safaricom.co.ke/mpesa/c2b/v1/registerurl'; // Sandbox
    // const url = 'https://api.safaricom.co.ke/mpesa/c2b/v1/registerurl'; // Live
  
    const requestBody = {
      ShortCode: 'YOUR_SHORTCODE',
      ResponseType: 'Completed', // or 'Cancelled'
      ConfirmationURL: 'https://yourwebsite.co.ke/confirmation-url',
      ValidationURL: 'https://yourwebsite.co.ke/validation-url',
    };
  
    try {
      const response = await axios.post(url, requestBody, {
        headers: {
          Authorization: \`Bearer \${token}\`,
          'Content-Type': 'application/json',
        },
      });
  
      console.log(response.data);
      return response.data;
    } catch (error) {
      console.error(\`Error: \${error.message}\`);
    }
  }
  \`\`\`
  
  ---
  
  ### HANDLING WEBHOOK BODY
  
  Finally, you need to handle the webhook body for completed transactions. Here's how you can do it in Node.js:
  
  \`\`\`javascript
  function handleWebhookBody(requestBody) {
    const transactionDetails = {
      TransactionType: requestBody.TransactionType,
      TransID: requestBody.TransID,
      TransTime: requestBody.TransTime,
      TransAmount: requestBody.TransAmount,
      BusinessShortCode: requestBody.BusinessShortCode,
      BillRefNumber: requestBody.BillRefNumber,
      MSISDN: requestBody.MSISDN,
      FirstName: requestBody.FirstName,
      MiddleName: requestBody.MiddleName,
      LastName: requestBody.LastName,
    };
  
    // Save the transaction details to a database or perform other actions
    console.log('Transaction Details:', transactionDetails);
  
    return { status: 'success' };
  }
  \`\`\`
  
  ---
  
  ### CONCLUSION
  
  Congratulations! You've successfully integrated M-Pesa Daraja API in Node.js. You can now send STK Push requests, handle callbacks, and manage offline payments using Paybill or Till numbers.
  `
    },


{
  id: 2,
  title: "Web Development",
  content: `

### Getting Started with Node.js for Building Websites

Node.js is a powerful runtime environment for building scalable and efficient web applications. Here's a step-by-step guide to get you started with Node.js for web development:

---

### 1. Set Up Your Development Environment
   - **Install Node.js**: Download and install Node.js from [nodejs.org](https://nodejs.org/). Node.js comes with **npm** (Node Package Manager), which is essential for managing dependencies.
   - **Install a Text Editor/IDE**: Use a code editor like **Visual Studio Code**, **Sublime Text**, or **WebStorm** for writing your Node.js code.
   - **Set Up a Local Server**: Node.js has a built-in HTTP module, but you can use frameworks like **Express.js** to simplify server setup.
   - **Database Setup**: Install a database like **MongoDB** (NoSQL) or **MySQL** (SQL) to manage your application's data.

---

### 2. Learn Node.js Basics
   - **Node.js Syntax**: Understand the basics of JavaScript, including variables, arrays, loops, functions, and conditionals.
     \`\`\`javascript
     console.log("Hello, World!");
     \`\`\`
   - **Data Types**: Learn about JavaScript data types such as strings, numbers, booleans, objects, and arrays.
   - **Modules**: Use Node.js modules to organize your code. For example:
     \`\`\`javascript
     // math.js
     exports.add = (a, b) => a + b;

     // app.js
     const math = require('./math');
     console.log(math.add(2, 3)); // Output: 5
     \`\`\`
   - **Asynchronous Programming**: Learn about callbacks, promises, and async/await for handling asynchronous operations.
     \`\`\`javascript
     const fs = require('fs');
     fs.readFile('file.txt', 'utf8', (err, data) => {
       if (err) throw err;
       console.log(data);
     });
     \`\`\`

---

### 3. Database Interaction
   - **Setting Up a Database**: Use MongoDB or MySQL to create a database.
     \`\`\`sql
     CREATE DATABASE my_database;
     \`\`\`
   - **Connect to Database**: Use libraries like **mongoose** (for MongoDB) or **mysql2** (for MySQL) to connect to your database.
     \`\`\`javascript
     // MongoDB with Mongoose
     const mongoose = require('mongoose');
     mongoose.connect('mongodb://localhost:27017/my_database', { useNewUrlParser: true, useUnifiedTopology: true });

     // MySQL with mysql2
     const mysql = require('mysql2');
     const connection = mysql.createConnection({
       host: 'localhost',
       user: 'root',
       password: 'password',
       database: 'my_database',
     });
     \`\`\`
   - **CRUD Operations**: Perform Create, Read, Update, and Delete operations.
     \`\`\`javascript
     // MongoDB Example
     const User = mongoose.model('User', { name: String, email: String });
     const user = new User({ name: 'John Doe', email: 'john@example.com' });
     user.save().then(() => console.log('User saved!'));

     // MySQL Example
     connection.query('INSERT INTO users (name, email) VALUES (?, ?)', ['John Doe', 'john@example.com'], (err, results) => {
       if (err) throw err;
       console.log('User saved!');
     });
     \`\`\`

---

### 4. Work with Sessions and Cookies
   - **Sessions**: Use libraries like **express-session** to manage user sessions.
     \`\`\`javascript
     const session = require('express-session');
     app.use(session({ secret: 'mysecret', resave: false, saveUninitialized: true }));
     app.get('/login', (req, res) => {
       req.session.username = 'JohnDoe';
       res.send('Logged in!');
     });
     \`\`\`
   - **Cookies**: Use the **cookie-parser** middleware to handle cookies.
     \`\`\`javascript
     const cookieParser = require('cookie-parser');
     app.use(cookieParser());
     app.get('/set-cookie', (req, res) => {
       res.cookie('user', 'JohnDoe', { maxAge: 3600000 }); // Expires in 1 hour
       res.send('Cookie set!');
     });
     \`\`\`

---

### 5. Learn About Node.js Frameworks (Optional)
   - **Why Use Frameworks?** Frameworks like **Express.js**, **NestJS**, or **Koa** provide structure and pre-built components for routing, middleware, and more.
   - **Start with Express.js**: Express is the most popular Node.js framework for building web applications and APIs.
     \`\`\`javascript
     const express = require('express');
     const app = express();
     app.get('/', (req, res) => {
       res.send('Hello, World!');
     });
     app.listen(3000, () => console.log('Server running on port 3000'));
     \`\`\`

---

### 6. Design Your Website (HTML & CSS)
   - **HTML**: Use HTML to create the structure of your website.
   - **CSS**: Style your website using CSS or frameworks like **Bootstrap** or **Tailwind CSS**.

---

### 7. Create Dynamic Content
   - **Dynamic Pages**: Use a templating engine like **EJS**, **Pug**, or **Handlebars** to render dynamic content.
     \`\`\`javascript
     // EJS Example
     app.set('view engine', 'ejs');
     app.get('/', (req, res) => {
       res.render('index', { title: 'Home', message: 'Welcome!' });
     });
     \`\`\`

---

### 8. Add Forms and Validation
   - **Create Forms**: Use HTML forms to collect user input.
   - **Validation**: Validate input using libraries like **express-validator**.
     \`\`\`javascript
     const { body, validationResult } = require('express-validator');
     app.post('/submit', [
       body('email').isEmail(),
       body('password').isLength({ min: 5 }),
     ], (req, res) => {
       const errors = validationResult(req);
       if (!errors.isEmpty()) {
         return res.status(400).json({ errors: errors.array() });
       }
       res.send('Form submitted successfully!');
     });
     \`\`\`

---

### 9. Security Measures
   - **Sanitize User Input**: Use libraries like **validator.js** to sanitize and validate user input.
   - **Password Hashing**: Use **bcrypt** to securely hash passwords.
     \`\`\`javascript
     const bcrypt = require('bcrypt');
     const saltRounds = 10;
     bcrypt.hash('myPassword', saltRounds, (err, hash) => {
       console.log('Hashed Password:', hash);
     });
     \`\`\`
   - **Prevent SQL Injection**: Use parameterized queries or ORMs like **Sequelize** (for SQL) or **Mongoose** (for MongoDB).

---

### 10. Testing Your Website
   - **Local Testing**: Use tools like **Postman** to test your APIs.
   - **Debugging**: Use **console.log()** or debugging tools in your IDE.
   - **Browser Testing**: Test your website on different browsers.

---

### 11. Deploy Your Website
   - **Choose a Hosting Provider**: Use platforms like **Heroku**, **Vercel**, or **AWS** to deploy your Node.js application.
   - **Upload Your Files**: Use Git or FTP to upload your application to the hosting server.
   - **Configure Environment Variables**: Use environment variables to store sensitive data like API keys.

---

### 12. Maintain and Update Your Website
   - **Bug Fixes**: Regularly fix bugs and issues.
   - **Security Updates**: Keep your dependencies updated using **npm audit**.

---

### Additional Tips:
   - **Learn Version Control**: Use Git to track changes and collaborate with others.
   - **Documentation**: Document your code for future reference.
   - **Community**: Engage with Node.js communities (e.g., StackOverflow, Reddit) to learn and ask questions.

By following this structured approach, you'll gain proficiency in building web applications with Node.js. Start small, build projects, and gradually explore advanced topics!
`
},

{
  id: 3,
  title: "SMS Integration",
  content: `
### SMS Integration Guide

#### Overview
Integrate SMS functionality into your application using the **UMSComms** API. This guide explains how to send SMS messages programmatically using Node.js.

---

### Step 1: Obtain API Credentials
To use the SMS API, you must have an **API key** and an **App ID**.  
- **Sign up** on [UMSComms](https://comms.umeskiasoftwares.com).
- **Generate an API key** and App ID from the developer dashboard.
- **Note down** the credentials for later use.

---

### Step 2: Configure API Credentials
Create a configuration file (config.js) to store your credentials securely:

\`\`\`javascript
// config.js
module.exports = {
  API_KEY: 'your_api_key',
  APP_ID: 'your_app_id',
  API_URL: 'https://comms.umeskiasoftwares.com/api/v1/sms/send',
};
\`\`\`

---

### Step 3: Sending an SMS
To send an SMS, make a **POST request** to the API endpoint. Below is a Node.js example using the **axios** library:

\`\`\`javascript
const axios = require('axios');
const config = require('./config');

async function sendSMS(phone, message) {
  try {
    // Append the default STOP message with three line breaks
    const defaultMessage = "\\n\\n\\nMuindi\\nSTOP *456*9*5#";
    const fullMessage = message + defaultMessage;

    // Send SMS request
    const response = await axios.post(config.API_URL, {
      api_key: config.API_KEY,
      app_id: config.APP_ID,
      sender_id: 'UMS_SMS', // Replace with your sender ID
      message: fullMessage,
      phone: phone,
    });

    // Check if SMS was sent successfully
    if (response.data.status === 'success') {
      console.log('SMS sent successfully!');
      console.log('Message ID:', response.data.message_id);
      console.log('Cost:', response.data.cost);
    } else {
      console.log('Failed to send SMS:', response.data.message);
    }
  } catch (error) {
    console.error('Error sending SMS:', error.message);
  }
}

// Example usage
sendSMS('254712345678', 'Hello, this is a test message!');
\`\`\`

---

### Step 4: Create an HTML Form
To allow users to send SMS, create a simple HTML form and handle the form submission using **Express.js**:

\`\`\`html
<!-- public/index.html -->
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Send SMS</title>
</head>
<body>
  <h1>Send SMS</h1>
  <form action="/send-sms" method="POST">
    <label for="phone">Phone Number:</label>
    <input type="text" id="phone" name="phone" required>
    
    <label for="message">Message:</label>
    <textarea id="message" name="message" required></textarea>

    <button type="submit">Send SMS</button>
  </form>
</body>
</html>
\`\`\`

---

### Step 5: Handle Form Submission with Express.js
Use **Express.js** to handle the form submission and send the SMS:

\`\`\`javascript
const express = require('express');
const axios = require('axios');
const config = require('./config');
const app = express();
const path = require('path');

// Middleware to parse form data
app.use(express.urlencoded({ extended: true }));
app.use(express.static('public'));

// Serve the HTML form
app.get('/', (req, res) => {
  res.sendFile(path.join(__dirname, 'public', 'index.html'));
});

// Handle form submission
app.post('/send-sms', async (req, res) => {
  const { phone, message } = req.body;

  try {
    // Append the default STOP message with three line breaks
    const defaultMessage = "\\n\\n\\nMuindi\\nSTOP *456*9*5#";
    const fullMessage = message + defaultMessage;

    // Send SMS request
    const response = await axios.post(config.API_URL, {
      api_key: config.API_KEY,
      app_id: config.APP_ID,
      sender_id: 'UMS_SMS', // Replace with your sender ID
      message: fullMessage,
      phone: phone,
    });

    // Check if SMS was sent successfully
    if (response.data.status === 'success') {
      res.send('SMS sent successfully!');
    } else {
      res.send('Failed to send SMS.');
    }
  } catch (error) {
    res.status(500).send('Error sending SMS: ' + error.message);
  }
});

// Start the server
const PORT = 3000;
app.listen(PORT, () => {
  console.log(\`Server running on http://localhost:\${PORT}\`);
});
\`\`\`

---

### Step 6: Handling API Response
The API response contains information about the status of the SMS request. A successful response looks like this:

\`\`\`json
{
  "status": "success",
  "message_id": "12345",
  "cost": "0.5"
}
\`\`\`

---

### Conclusion
With these steps, you can successfully integrate SMS functionality into your Node.js application using the **UMSComms API**. You can now send SMS messages programmatically and handle user input via a web form.

Happy coding! 🚀
`
},
{
  id: 4,
  title: "Mailers",
  content: `
### Node.js Mailers Guide

#### Overview
Sending emails in Node.js can be done using libraries like **Nodemailer**, which provides advanced email functionalities, including:
- Sending emails via SMTP (e.g., Gmail, Outlook, custom mail servers).
- Sending HTML emails.
- Adding attachments.
- Using authentication with secure protocols (TLS/SSL).

---

### Step 1: Installing Nodemailer
To use **Nodemailer**, you need to install it via **npm** (Node Package Manager). Run the following command in your terminal:

\`\`\`sh
npm install nodemailer
\`\`\`

---

### Step 2: Configuring Nodemailer
After installing Nodemailer, create a Node.js script to send an email:

\`\`\`javascript
// Import Nodemailer
const nodemailer = require('nodemailer');

// Create a transporter object using SMTP
const transporter = nodemailer.createTransport({
  host: 'smtp.example.com', // Replace with your SMTP server
  port: 587, // Common SMTP port
  secure: false, // Use TLS
  auth: {
    user: 'your_email@example.com', // SMTP username
    pass: 'your_password', // SMTP password
  },
});

// Email content
const mailOptions = {
  from: 'your_email@example.com', // Sender address
  to: 'recipient@example.com', // Recipient address
  subject: 'Test Email from Nodemailer', // Email subject
  text: 'Hello! This is a test email sent using Nodemailer.', // Plain text body
  html: '<p>Hello! This is a <b>test email</b> sent using Nodemailer.</p>', // HTML body
};

// Send email
transporter.sendMail(mailOptions, (error, info) => {
  if (error) {
    console.error('Error sending email:', error);
  } else {
    console.log('Email sent successfully!');
    console.log('Message ID:', info.messageId);
  }
});
\`\`\`

---

### Step 3: Sending Emails with Gmail
If you want to use **Gmail** as your SMTP server, you can configure Nodemailer as follows:

\`\`\`javascript
const nodemailer = require('nodemailer');

// Create a transporter object using Gmail
const transporter = nodemailer.createTransport({
  service: 'gmail',
  auth: {
    user: 'your_email@gmail.com', // Your Gmail address
    pass: 'your_app_password', // Use an App Password (not your Gmail password)
  },
});

// Email content
const mailOptions = {
  from: 'your_email@gmail.com', // Sender address
  to: 'recipient@example.com', // Recipient address
  subject: 'Test Email from Nodemailer (Gmail)', // Email subject
  text: 'Hello! This is a test email sent using Nodemailer and Gmail.', // Plain text body
  html: '<p>Hello! This is a <b>test email</b> sent using Nodemailer and Gmail.</p>', // HTML body
};

// Send email
transporter.sendMail(mailOptions, (error, info) => {
  if (error) {
    console.error('Error sending email:', error);
  } else {
    console.log('Email sent successfully!');
    console.log('Message ID:', info.messageId);
  }
});
\`\`\`

**Note:** For Gmail, you need to generate an **App Password** if you have 2-Step Verification enabled. Go to your Google Account > Security > App Passwords to generate one.

---

### Step 4: Adding Attachments
You can add attachments to your email using the (attachments) option:

\`\`\`javascript
const mailOptions = {
  from: 'your_email@example.com',
  to: 'recipient@example.com',
  subject: 'Test Email with Attachment',
  text: 'Hello! This email contains an attachment.',
  html: '<p>Hello! This email contains an <b>attachment</b>.</p>',
  attachments: [
    {
      filename: 'example.txt', // Name of the attachment
      content: 'This is a text file attachment.', // Content of the attachment
    },
    {
      filename: 'image.png', // Name of the attachment
      path: '/path/to/image.png', // Path to the file
    },
  ],
};

// Send email
transporter.sendMail(mailOptions, (error, info) => {
  if (error) {
    console.error('Error sending email:', error);
  } else {
    console.log('Email sent successfully!');
    console.log('Message ID:', info.messageId);
  }
});
\`\`\`

---

### Step 5: Running the Script
To run the script, use the following command in your terminal:

\`\`\`sh
node send_mail.js
\`\`\`

---

### Conclusion
**Nodemailer** makes it easy to send emails in Node.js with support for SMTP, HTML emails, and attachments. Whether you're using a custom SMTP server or Gmail, Nodemailer provides a simple and flexible API for sending emails programmatically.

Happy coding! 🚀
`
}
    
  ];

  const toggleExpand = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const handleClickOutside = (event) => {
    if (contentRef.current && !contentRef.current.contains(event.target)) {
      setExpanded(null);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="php-documentation">
      <h1 className="typing-text">{displayedText}</h1>

      <div className="service-list">
        {services.map((service) => (
          <div key={service.id} className="service-item">
            <button className="service-btn" onClick={() => toggleExpand(service.id)}>
              {service.id}. {service.title} <FaArrowRight className="arrow-icon" />
            </button>

            {expanded === service.id && (
              <div className="service-content" ref={contentRef}>
                <div className="content-wrapper">
                  <pre>
                    <code>{service.content}</code>
                  </pre>
                  
                </div>
                <div className="navigation-buttons">
                  <button className="nav-btn" onClick={() => setExpanded(service.id - 1)}>
                    <FaArrowLeft /> Back Service
                  </button>
                  <button className="nav-btn" onClick={() => setExpanded(service.id + 1)}>
                  Next Service <FaNext />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default JavaScript;